/**
 * ConfigContext — fetches GET /api/v1/config exactly once at app start
 * and provides it to descendants. Drives the upload pipeline (s3_mode,
 * max_image_size_mb) and any future client-side gating that depends on
 * deploy-time settings.
 *
 * Standalone mode: backend always serves /api/v1/config so this works
 * the same way locally and on remote-dev/production.
 *
 * If the fetch fails (offline, backend down at startup) the consumer
 * sees `loading=false, config=null, error=<message>` and can decide
 * what to render. Today only the upload modal cares, and it falls back
 * to the legacy single-shot POST /images endpoint (still mounted) until
 * config resolves.
 */
import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { configApi } from '../services/annotatorApi';
import type { PublicConfig } from '../types/api';

// configApi is imported once at the top; both ConfigProvider's mount
// fetch and useOrgConfig's effect call methods on it.

interface ConfigState {
    loading: boolean;
    config: PublicConfig | null;
    error: string | null;
}

const ConfigContext = createContext<ConfigState>({
    loading: true,
    config: null,
    error: null,
});

export function ConfigProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<ConfigState>({
        loading: true,
        config: null,
        error: null,
    });

    useEffect(() => {
        let cancelled = false;
        configApi
            .get()
            .then((config) => {
                if (cancelled) return;
                setState({ loading: false, config, error: null });
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                const message = err instanceof Error ? err.message : String(err);
                setState({ loading: false, config: null, error: message });
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return <ConfigContext.Provider value={state}>{children}</ConfigContext.Provider>;
}

export function useConfig(): ConfigState {
    return useContext(ConfigContext);
}

/**
 * useOrgConfig — like useConfig() but fetches the org-scoped
 * `/api/v1/org/{orgId}/config` endpoint so callers see EFFECTIVE quotas
 * (per-org override → platform default → env fallback). When `orgId` is
 * empty/null, falls through to the platform-default ConfigContext.
 *
 * Use when the caller already has an orgId and needs accurate per-org
 * quota values for client-side validation (upload modal size cap, class
 * editor counter, etc).
 */
export function useOrgConfig(orgId: string | null | undefined): ConfigState {
    const platform = useConfig();
    const [state, setState] = useState<ConfigState>({
        loading: true,
        config: null,
        error: null,
    });

    useEffect(() => {
        if (!orgId) {
            setState({ loading: platform.loading, config: platform.config, error: platform.error });
            return;
        }
        let cancelled = false;
        setState((prev) => ({ ...prev, loading: true, error: null }));
        configApi
            .getForOrg(orgId)
            .then((config) => {
                if (cancelled) return;
                setState({ loading: false, config, error: null });
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                // Fall back to platform defaults rather than blanking — caller
                // can still render upload caps using the unauth values.
                const message = err instanceof Error ? err.message : String(err);
                setState({ loading: false, config: platform.config, error: message });
            });
        return () => {
            cancelled = true;
        };
    }, [orgId, platform.loading, platform.config, platform.error]);

    return state;
}
